import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from '../firebase';
import styles from './MentionAutocomplete.module.css';

function MentionAutocomplete({
  value,
  onChange,
  onSelect,
  position = { top: 0, left: 0 },
  inputRef,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [hairstylists, setHairstylists] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [fixedPosition, setFixedPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchHairstylists = async () => {
      try {
        const q = query(collection(db, 'hairstylists'), limit(200));
        const snapshot = await getDocs(q);
        const stylists = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || '',
            profilePhoto: data.profilePhoto || null,
          };
        });
        setHairstylists(stylists);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching hairstylists:', error);
      }
    };
    fetchHairstylists();
  }, []);

  useEffect(() => {
    if (!value) {
      setSuggestions([]);
      return;
    }

    // Détecter @ suivi d'au moins 3 caractères
    const mentionMatch = value.match(/@(\w{3,})$/);
    if (mentionMatch) {
      const searchTerm = mentionMatch[1].toLowerCase();
      const filtered = hairstylists
        .filter((stylist) => stylist.name.toLowerCase().includes(searchTerm))
        .slice(0, 3);
      setSuggestions(filtered);
      setSelectedIndex(0);
    } else {
      setSuggestions([]);
    }
  }, [value, hairstylists]);

  const handleSelect = (stylist) => {
    if (!value) return;
    
    // Trouver la position du @ le plus récent
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex === -1) return;
    
    // Remplacer @nom par @[nom](id)
    const beforeMention = value.substring(0, lastAtIndex);
    const afterMention = value.substring(lastAtIndex);
    const mentionMatch = afterMention.match(/^@(\w+)/);
    
    if (mentionMatch) {
      const newValue = `${beforeMention}@[${stylist.name}](${stylist.id}) `;
      onChange(newValue);
      onSelect && onSelect(stylist);
      setSuggestions([]);
    }
  };

  const handleKeyDown = (e) => {
    if (suggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && suggestions[selectedIndex]) {
      e.preventDefault();
      handleSelect(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setSuggestions([]);
    }
  };

  // Calculer la position fixe basée sur la position relative
  useEffect(() => {
    if (inputRef?.current && suggestions.length > 0) {
      const input = inputRef.current;
      const rect = input.getBoundingClientRect();
      setFixedPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
      });
    }
  }, [inputRef, suggestions.length, position]);

  // Exposer handleKeyDown pour l'utiliser dans le composant parent
  useEffect(() => {
    if (inputRef?.current && suggestions.length > 0) {
      const input = inputRef.current;
      const keyHandler = (e) => {
        if (suggestions.length === 0) return;
        
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === 'Enter' && suggestions[selectedIndex]) {
          e.preventDefault();
          handleSelect(suggestions[selectedIndex]);
        } else if (e.key === 'Escape') {
          setSuggestions([]);
        }
      };
      input.addEventListener('keydown', keyHandler);
      return () => {
        input.removeEventListener('keydown', keyHandler);
      };
    }
  }, [inputRef, suggestions, selectedIndex]);

  if (suggestions.length === 0) return null;

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={styles.dropdown}
      style={{
        top: `${fixedPosition.top}px`,
        left: `${fixedPosition.left}px`,
        position: 'fixed',
      }}
    >
      {suggestions.map((stylist, index) => (
        <button
          key={stylist.id}
          type="button"
          className={`${styles.suggestion} ${index === selectedIndex ? styles.selected : ''}`}
          onClick={() => handleSelect(stylist)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          {stylist.profilePhoto ? (
            <img src={stylist.profilePhoto} alt={stylist.name} className={styles.suggestionAvatar} />
          ) : (
            <div className={styles.suggestionAvatar} style={{ background: '#B19CD9', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
              {stylist.name?.[0] || '?'}
            </div>
          )}
          <span>{stylist.name}</span>
        </button>
      ))}
    </div>
  );

  return createPortal(dropdownContent, document.body);
}

export default MentionAutocomplete;

